// TODO: Refactor queryFn to be more readable

import { useQuery } from 'react-query';
import {
  filterHandpickedArticleByWordCount,
  getAlgoliaListFilters,
  getIsAutomatic,
} from 'containers/EditorList/utils';
import {
  filterHandpickedResourceByCutoff,
  filterClientExclusiveResources,
} from 'containers/Resources/utils';
import { getContenfulFilters } from 'containers/Main/utils';
import useSiteCopySelector from 'components/useSiteCopySelector';
import useAlgoliaLocale from 'components/useAlgoliaLocale';
import useContentfulLocale from 'components/useContentfulLocale';
import useAlgoliaLocalizedLanguages from 'components/useAlgoliaLocalizedLanguages';
import ctf from 'utils/contentfulService';
import { format } from 'date-fns';
import { DATE_FORMAT } from 'utils/constants';
import _get from 'lodash/get';
import _has from 'lodash/has';
import _forEach from 'lodash/forEach';
import _sortBy from 'lodash/sortBy';
import _isEmpty from 'lodash/isEmpty';
import _uniq from 'lodash/uniq';
import _flatten from 'lodash/flatten';
import Config from 'utils/getEnvConfig';
import algolia from 'utils/algoliaService';
import {
  isWithinPromotionDate,
  hasLanguageSpecificResources,
} from '../helpers';

const {
  ALGOLIA: { NEWS_INDEX, RESOURCES_AVG_RATED_INDEX },
} = Config;

/* eslint-disable  no-param-reassign */
const queryFn = async (
  _key,
  {
    clientId,
    clientDetails,
    clientShortName,
    clientExcludedTopic,
    clientExcludedResourceTypes,
    clientExcludedCost,
    clientListIds,
    clientAudienceIds,
    listCount,
    languages,
    averageRatingCutoff,
    expertRatingCutoff,
    userRatingCutoff,
    excludedAssessments,
    localeFilters,
    algoliaLocale,
    algoliaResourceTypeMapping,
    shouldFilterShortArticles,
    featuresConfig,
    filtersTranslationsSiteCopy,
  },
) => {
  const contentfulFilters = getContenfulFilters(
    clientExcludedTopic,
    averageRatingCutoff,
    expertRatingCutoff,
    userRatingCutoff,
    false,
    true,
  );
  const now = Date.now();
  const queries = [
    ctf.getEntries({
      content_type: 'list',
      'fields.reviewStatus': 'Accepted',
      'sys.id[in]': clientListIds.join(','),
      include: 3,
      ...localeFilters,
    }),
  ];
  if (listCount > 0) {
    queries.push(
      ctf.getEntries({
        content_type: 'list',
        'fields.reviewStatus': 'Accepted',
        'fields.client[exists]': 'false',
        'fields.audienceType.sys.id[in]': clientAudienceIds.join(','),
        'sys.id[nin]': clientListIds.join(','),
        'fields.promotionDate[lte]': format(now, DATE_FORMAT),
        'fields.promotionEndDate[gte]': format(now, DATE_FORMAT),
        limit: listCount,
        include: 3,
        order: '-fields.calculatedAverageRating',
        ...contentfulFilters,
        ...localeFilters,
      }),
    );
    queries.push(
      ctf.getEntries({
        content_type: 'list',
        'fields.reviewStatus': 'Accepted',
        'fields.client[exists]': 'false',
        'fields.audienceType.sys.id[nin]': clientAudienceIds.join(','),
        'sys.id[nin]': clientListIds.join(','),
        'fields.promotionDate[lte]': format(now, DATE_FORMAT),
        'fields.promotionEndDate[gte]': format(now, DATE_FORMAT),
        limit: listCount,
        include: 3,
        order: '-fields.calculatedAverageRating',
        ...contentfulFilters,
        ...localeFilters,
      }),
    );
  }

  const response = await Promise.all(queries);

  const promotionLists = _get(response, '2.items', []);
  const listIdsWithinPromoList = promotionLists.reduce((res, next) => {
    _get(next.fields, 'resources', []).forEach(item => {
      const isList = _get(item.sys, 'contentType.sys.id') === 'list';
      if (isList) {
        res[item.sys.id] = true;
      }
    });

    return res;
  }, {});
  const filteredPromoLists = promotionLists.filter(
    list => !listIdsWithinPromoList[list.sys.id],
  );

  let commonLists;
  if (clientId) {
    commonLists = _get(response, '3.items', []).concat(
      _get(response, '1.items', []),
      filteredPromoLists,
    );
  } else {
    commonLists = _get(response, '1.items', []).concat(filteredPromoLists);
  }
  const finalFirstResponse =
    clientListIds.length > 1
      ? _sortBy(_get(response, '0.items', []), item =>
          clientListIds.indexOf(_get(item, 'sys.id')),
        )
      : _get(response, '0.items', []);
  const responseLists = finalFirstResponse.concat(
    commonLists.slice(0, listCount),
  );
  responseLists.forEach(list => {
    list.fields.resources = _get(list, 'fields.resources', []).filter(r =>
      isWithinPromotionDate(list, now, r),
    );
  });
  const listsFromResources = responseLists.flatMap(list =>
    (_get(list, 'fields.resources') || []).filter(
      r => _get(r, 'sys.contentType.sys.id') === 'list',
    ),
  );
  const finalLists = responseLists.concat(listsFromResources);
  const automaticLists = finalLists.filter(item => getIsAutomatic(item));

  const resourcesQueries = automaticLists.map(item => {
    const context = _get(item, 'fields.context', '');
    const searchTerm = _get(context, 'searchTerm', '');
    const types = _get(item, 'fields.resourceTypes', []).map(type =>
      _get(algoliaResourceTypeMapping, type, type),
    );

    const topics = _get(
      item,
      'fields.whatHelpsTopics',
      _get(item, 'fields.topics', []),
    ).map(listTopic => _get(listTopic, 'fields.title'));

    const themesTopics = _uniq(
      _flatten(
        _get(
          item,
          'fields.whatHelpsThemes',
          _get(item, 'fields.themes', []),
        ).map(listTheme => _get(listTheme, 'fields.topics')),
      )
        .filter(
          themeTopic => _get(themeTopic, 'fields.reviewStatus') === 'Accepted',
        )
        .map(themeTopic => _get(themeTopic, 'fields.title')),
    );

    const audiences = _get(item, 'fields.audienceType', [])
      .filter(audience =>
        ['Accepted', 'Internal'].includes(
          _get(audience, 'fields.reviewStatus'),
        ),
      )
      .map(audience => _get(audience, 'fields.name'));

    const tags = _get(item, 'fields.tags', [])
      .filter(tag =>
        ['Accepted', 'Internal'].includes(_get(tag, 'fields.reviewStatus')),
      )
      .map(tag => _get(tag, 'fields.title'));

    const handpicked = _get(item, 'fields.resources', [])
      .map(resource => _get(resource, 'fields.slug'))
      .filter(resource => !!resource);

    let definitiveIndex;
    if (types.length === 1 && types.includes('News')) {
      definitiveIndex = NEWS_INDEX;
    } else {
      definitiveIndex = RESOURCES_AVG_RATED_INDEX;
    }

    const params = {
      context,
      types,
      topics,
      themesTopics,
      audiences,
      handpicked,
      locale: algoliaLocale,
      tags,
      clientDetails,
      clientExcludedTopic,
      clientExcludedResourceTypes,
      clientExcludedCost,
      averageRatingCutoff,
      expertRatingCutoff,
      userRatingCutoff,
      excludedAssessments,
      shouldFilterShortArticles,
      featuresConfig,
      filtersTranslationsSiteCopy,
    };

    return {
      indexName: definitiveIndex,
      query: searchTerm,
      params: {
        page: 0,
        hitsPerPage: 10,
        getRankingInfo: false,
        filters: getAlgoliaListFilters(params, definitiveIndex),
      },
    };
  });

  _forEach(finalLists, list => {
    if (Array.isArray(_get(list, 'fields.resources'))) {
      list.fields.resources = filterClientExclusiveResources({
        resources: _get(list, 'fields.resources'),
        clientShortName,
        clientGroupShortName: _get(clientDetails, 'clientGroup.shortName'),
      }).filter(
        resource => _get(resource, 'fields.reviewStatus') === 'Accepted',
      );

      if (clientExcludedCost) {
        list.fields.resources = _get(list, 'fields.resources').filter(
          resource =>
            !clientExcludedCost.includes(
              _get(resource, 'fields.costDescription'),
            ),
        );
      }
      list.fields.resources = _get(list, 'fields.resources').filter(resource =>
        filterHandpickedResourceByCutoff({
          resource,
          averageRatingCutoff,
          userRatingCutoff,
          expertRatingCutoff,
        }),
      );
      if (shouldFilterShortArticles) {
        list.fields.resources = _get(list, 'fields.resources').filter(
          resource =>
            filterHandpickedArticleByWordCount(resource, featuresConfig),
        );
      }
    }
  });

  let algoliaQueries = {};
  if (resourcesQueries.length > 0) {
    const resourcesResponse = await algolia.multipleQueries(resourcesQueries);
    algoliaQueries = automaticLists.reduce(
      (res, resource, index) => ({
        ...res,
        [resource.sys.id]: resourcesResponse.results[index].hits,
      }),
      {},
    );
  }

  responseLists.forEach(list => {
    const ownResources = _isEmpty(languages)
      ? _get(list, 'fields.resources') || []
      : (_get(list, 'fields.resources') || []).filter(resource => {
          const resourcesFromAlgolia = algoliaQueries[resource.sys.id] || [];
          if (resourcesFromAlgolia.length) {
            return true;
          }

          if (_has(resource, 'fields.language')) {
            const resourceLanguages = _get(resource, 'fields.language');
            if (resourceLanguages) {
              return hasLanguageSpecificResources(resourceLanguages, languages);
            }

            return _get(resource, 'fields.resources', []).some(r =>
              hasLanguageSpecificResources(
                _get(r, 'fields.language', []),
                languages,
              ),
            );
          }
          return true;
        });

    const algoliaResources = algoliaQueries[list.sys.id] || [];
    list.fields.resources = [...ownResources, ...algoliaResources];
  });

  if (!_isEmpty(excludedAssessments)) {
    const excludedAssessmentIDs = excludedAssessments.map(
      assessment => assessment.sys.id,
    );
    _forEach(finalLists, list => {
      list.fields.resources = (_get(list, 'fields.resources') || []).filter(
        resource => !excludedAssessmentIDs.includes(_get(resource, 'sys.id')),
      );
    });
  }

  return responseLists;
};

export const useGetLandingLists = options_ => {
  const localeFilters = useContentfulLocale();
  const algoliaLocale = useAlgoliaLocale();
  const languages = useAlgoliaLocalizedLanguages();
  const [filtersTranslationsSiteCopy] = useSiteCopySelector([
    'filters-translations',
  ]);

  const options = {
    ...options_,
    localeFilters,
    algoliaLocale,
    languages,
    filtersTranslationsSiteCopy,
  };

  const queryInfo = useQuery(['h-lists', options], queryFn);

  return queryInfo;
};
